'use client'

import { useState, useMemo } from 'react'
import Link from 'next/link'
import { useAuth } from '@/contexts/AuthContext'
import { TierGate } from '@/components/app'
import { PERIODS, MEDIUMS, type PeriodId, type MediumId } from '@/lib/ontology'
import { getArtistInitials, getArtistColorVar } from '@/lib/artistAvatar'

// =============================================================================
// DATA - All market data organized for dynamic filtering
// =============================================================================

// Period data varies by selected medium
type PeriodMarketData = {
  id: PeriodId
  name: string
  dateRange: string
  volumes: Record<'all' | MediumId, string>
  lots: Record<'all' | MediumId, string>
  changes: Record<'all' | MediumId, number>
  confidence: number
  data: number[]
}

const periodData: PeriodMarketData[] = [
  {
    id: 'contemporary',
    name: 'Contemporary',
    dateRange: '1970+',
    volumes: { all: '$4.2B', paintings: '$2.8B', sculpture: '$680M', prints: '$420M', photography: '$180M', drawings: '$95M', 'mixed-media': '$25M' },
    lots: { all: '124K', paintings: '48K', sculpture: '18K', prints: '32K', photography: '14K', drawings: '10K', 'mixed-media': '2K' },
    changes: { all: 12.4, paintings: 14.2, sculpture: 8.8, prints: 18.6, photography: 6.2, drawings: 9.5, 'mixed-media': 22.1 },
    confidence: 96,
    data: [20, 25, 22, 30, 28, 35, 40, 38, 45],
  },
  {
    id: 'post-war',
    name: 'Post-War',
    dateRange: '1945-70',
    volumes: { all: '$2.1B', paintings: '$1.4B', sculpture: '$380M', prints: '$180M', photography: '$85M', drawings: '$48M', 'mixed-media': '$7M' },
    lots: { all: '67K', paintings: '28K', sculpture: '12K', prints: '16K', photography: '6K', drawings: '4K', 'mixed-media': '1K' },
    changes: { all: 8.2, paintings: 9.5, sculpture: 6.4, prints: 12.1, photography: 4.8, drawings: 5.2, 'mixed-media': 8.9 },
    confidence: 91,
    data: [40, 38, 42, 35, 38, 40, 42, 45, 48],
  },
  {
    id: 'modern',
    name: 'Modern',
    dateRange: '1880-1945',
    volumes: { all: '$3.8B', paintings: '$2.6B', sculpture: '$520M', prints: '$380M', photography: '$120M', drawings: '$165M', 'mixed-media': '$15M' },
    lots: { all: '89K', paintings: '42K', sculpture: '14K', prints: '18K', photography: '8K', drawings: '6K', 'mixed-media': '1K' },
    changes: { all: 5.1, paintings: 6.2, sculpture: 3.8, prints: 8.4, photography: 2.1, drawings: 4.5, 'mixed-media': 5.8 },
    confidence: 94,
    data: [30, 32, 28, 35, 40, 38, 42, 45, 48],
  },
  {
    id: '19th-century',
    name: '19th Century',
    dateRange: '1800-1880',
    volumes: { all: '$1.2B', paintings: '$920M', sculpture: '$145M', prints: '$65M', photography: '$28M', drawings: '$38M', 'mixed-media': '$4M' },
    lots: { all: '28K', paintings: '16K', sculpture: '4K', prints: '4K', photography: '2K', drawings: '1.5K', 'mixed-media': '0.5K' },
    changes: { all: 3.4, paintings: 4.1, sculpture: 2.2, prints: 5.8, photography: 1.5, drawings: 2.8, 'mixed-media': 3.2 },
    confidence: 92,
    data: [25, 26, 24, 27, 28, 26, 29, 30, 31],
  },
  {
    id: 'old-masters',
    name: 'Old Masters',
    dateRange: 'pre-1800',
    volumes: { all: '$1.4B', paintings: '$1.1B', sculpture: '$180M', prints: '$45M', photography: '$0', drawings: '$72M', 'mixed-media': '$3M' },
    lots: { all: '34K', paintings: '18K', sculpture: '6K', prints: '5K', photography: '0', drawings: '4K', 'mixed-media': '1K' },
    changes: { all: -2.1, paintings: -1.8, sculpture: -3.2, prints: 1.2, photography: 0, drawings: -0.8, 'mixed-media': -4.5 },
    confidence: 93,
    data: [30, 28, 29, 27, 26, 25, 24, 23, 22],
  },
]

// Medium overview data
const mediumOverview = [
  { id: 'paintings' as MediumId, name: 'Paintings', volume: '$8.82B', share: 58, change: 8.4, lots: '152K', avgHammer: '$58K' },
  { id: 'sculpture' as MediumId, name: 'Sculpture', volume: '$1.91B', share: 12, change: 5.2, lots: '54K', avgHammer: '$35K' },
  { id: 'prints' as MediumId, name: 'Prints', volume: '$1.09B', share: 7, change: 12.8, lots: '75K', avgHammer: '$14.5K' },
  { id: 'photography' as MediumId, name: 'Photography', volume: '$413M', share: 3, change: 4.2, lots: '30K', avgHammer: '$13.8K' },
  { id: 'drawings' as MediumId, name: 'Drawings', volume: '$418M', share: 3, change: 6.1, lots: '25.5K', avgHammer: '$16.4K' },
  { id: 'mixed-media' as MediumId, name: 'Mixed Media', volume: '$54M', share: 0.4, change: 15.4, lots: '5.5K', avgHammer: '$9.8K' },
]

// Upcoming auctions
const upcomingAuctions = [
  { house: "Christie's", title: 'Post-War & Contemporary Art Evening Sale', date: 'Mar 12, 2026', estimate: '$180-250M', lots: 48, location: 'New York', featured: true },
  { house: "Sotheby's", title: 'Impressionist & Modern Art', date: 'Mar 14, 2026', estimate: '$120-160M', lots: 156, location: 'London', featured: true },
  { house: 'Phillips', title: '20th Century & Contemporary Art', date: 'Mar 20, 2026', estimate: '$40-60M', lots: 78, location: 'Hong Kong', featured: false },
  { house: 'Bonhams', title: 'Post-War & Contemporary Art', date: 'Mar 28, 2026', estimate: '$20-30M', lots: 102, location: 'Los Angeles', featured: false },
  { house: "Christie's", title: 'Old Masters Evening Sale', date: 'Apr 2, 2026', estimate: '$80-120M', lots: 42, location: 'London', featured: false },
  { house: "Sotheby's", title: 'Contemporary Art Day Sale', date: 'Apr 5, 2026', estimate: '$25-35M', lots: 186, location: 'New York', featured: false },
]

// Recent notable sales - filtered by medium in production
const recentSales = [
  { id: 'rs1', artist: 'Claude Monet', artistSlug: 'claude-monet', work: 'Nymphéas (Water Lilies)', house: "Christie's", hammer: '$74M', estimate: '$65-85M', premium: '+12%', date: '3 days ago', medium: 'paintings' as MediumId, era: 'modern' as PeriodId },
  { id: 'rs2', artist: 'Jean-Michel Basquiat', artistSlug: 'jean-michel-basquiat', work: 'Untitled (Devil)', house: "Christie's", hammer: '$42.4M', estimate: '$40-50M', premium: '+6%', date: '5 days ago', medium: 'paintings' as MediumId, era: 'contemporary' as PeriodId },
  { id: 'rs3', artist: 'Andy Warhol', artistSlug: 'andy-warhol', work: 'Shot Sage Blue Marilyn', house: "Christie's", hammer: '$28.5M', estimate: '$25-35M', premium: '+14%', date: '1 week ago', medium: 'prints' as MediumId, era: 'post-war' as PeriodId },
  { id: 'rs4', artist: 'Jeff Koons', artistSlug: 'jeff-koons', work: 'Balloon Dog (Orange)', house: "Sotheby's", hammer: '$24.8M', estimate: '$20-30M', premium: '+24%', date: '1 week ago', medium: 'sculpture' as MediumId, era: 'contemporary' as PeriodId },
  { id: 'rs5', artist: 'Mark Rothko', artistSlug: 'mark-rothko', work: 'No. 7', house: "Sotheby's", hammer: '$18.2M', estimate: '$15-20M', premium: '+21%', date: '2 weeks ago', medium: 'paintings' as MediumId, era: 'post-war' as PeriodId },
  { id: 'rs6', artist: 'Cindy Sherman', artistSlug: 'cindy-sherman', work: 'Untitled Film Still #21', house: 'Phillips', hammer: '$3.2M', estimate: '$2.5-3.5M', premium: '+28%', date: '2 weeks ago', medium: 'photography' as MediumId, era: 'contemporary' as PeriodId },
]

// Artist avatar component
function ArtistAvatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  const initials = getArtistInitials(name)
  const colorVar = getArtistColorVar(name)

  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-sm',
  }

  return (
    <div
      className={`${sizeClasses[size]} rounded-lg flex items-center justify-center font-medium text-white flex-shrink-0`}
      style={{ background: `var(${colorVar})` }}
    >
      {initials}
    </div>
  )
}

// Geographic data
const geographicData = [
  { region: 'Americas', volume: '$5.2B', share: 38, change: 8.4, topCities: ['New York', 'Los Angeles', 'Miami'] },
  { region: 'Europe', volume: '$4.1B', share: 30, change: 5.2, topCities: ['London', 'Paris', 'Geneva'] },
  { region: 'Asia-Pacific', volume: '$3.4B', share: 25, change: 15.8, topCities: ['Hong Kong', 'Beijing', 'Tokyo'] },
  { region: 'Middle East', volume: '$0.9B', share: 7, change: 22.1, topCities: ['Dubai', 'Doha', 'Tel Aviv'] },
]

// Time range options
const timeRanges = [
  { id: '30d', name: '30 days' },
  { id: '12m', name: '12 months' },
  { id: 'ytd', name: 'YTD' },
  { id: 'all', name: 'All time' },
]

// =============================================================================
// COMPONENTS
// =============================================================================

function Sparkline({ data, positive }: { data: number[]; positive: boolean }) {
  const max = Math.max(...data)
  const min = Math.min(...data)
  const range = max - min || 1
  const width = 80
  const height = 32

  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width
    const y = height - ((v - min) / range) * height
    return `${x},${y}`
  }).join(' ')

  const areaPoints = `0,${height} ${points} ${width},${height}`

  return (
    <svg width={width} height={height} className="overflow-visible">
      <polygon
        points={areaPoints}
        fill={positive ? 'var(--positive)' : 'var(--negative)'}
        opacity={0.1}
      />
      <polyline
        points={points}
        fill="none"
        stroke={positive ? 'var(--positive)' : 'var(--negative)'}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function MediumIcon({ medium, className = '' }: { medium: MediumId; className?: string }) {
  const iconClass = `${className}`

  switch (medium) {
    case 'paintings':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="5" width="18" height="14" rx="1" />
          <path d="M3 15l5-4 3 2 5-4 5 4" />
          <circle cx="16" cy="9" r="1.5" />
        </svg>
      )
    case 'sculpture':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="8" r="4" />
          <ellipse cx="12" cy="19" rx="6" ry="2" />
          <line x1="12" y1="12" x2="12" y2="17" />
        </svg>
      )
    case 'prints':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="6" y="3" width="12" height="15" rx="1" />
          <rect x="8" y="5" width="8" height="9" rx="0.5" />
          <line x1="8" y1="16" x2="16" y2="16" />
          <line x1="8" y1="19" x2="16" y2="19" />
          <line x1="10" y1="21" x2="14" y2="21" />
        </svg>
      )
    case 'photography':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="2" y="6" width="20" height="14" rx="2" />
          <circle cx="12" cy="13" r="4" />
          <circle cx="12" cy="13" r="2" />
          <path d="M6 6V5a1 1 0 011-1h2l1 2" />
        </svg>
      )
    case 'drawings':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M4 18 Q8 8, 12 14 T20 10" strokeLinecap="round" />
          <path d="M6 20 Q10 16, 14 18" strokeLinecap="round" opacity="0.5" />
          <circle cx="18" cy="8" r="2" />
        </svg>
      )
    case 'mixed-media':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="4" y="8" width="10" height="10" rx="1" />
          <circle cx="17" cy="11" r="5" />
          <polygon points="12,4 16,10 8,10" />
        </svg>
      )
    default:
      return null
  }
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function MarketPage() {
  const [selectedMedium, setSelectedMedium] = useState<'all' | MediumId>('all')
  const [timeRange, setTimeRange] = useState('12m')
  const { user, canAccessHistory, canExport } = useAuth()

  const isHistoryLimited = !canAccessHistory(1)
  const isFreeUser = user?.tier === 'free' || !user

  // Filter data based on selected medium
  const filteredPeriodData = useMemo(() => {
    return periodData.map(p => ({
      ...p,
      volume: p.volumes[selectedMedium],
      lots: p.lots[selectedMedium],
      change: p.changes[selectedMedium],
    }))
  }, [selectedMedium])

  const filteredSales = useMemo(() => {
    if (selectedMedium === 'all') return recentSales
    return recentSales.filter(s => s.medium === selectedMedium)
  }, [selectedMedium])

  // AI Market Outlook - expanded with tier-specific content
  const aiOutlook = {
    sentiment: 'bullish' as const,
    confidence: 94,
    summary: selectedMedium === 'all'
      ? "The global art market shows strong momentum heading into Q1 2026. Contemporary art leads with 12.4% YoY growth, driven by Street Art and Neo-Expressionism. Asian buyers are increasingly active, particularly for blue-chip works."
      : `The ${MEDIUMS.find(m => m.id === selectedMedium)?.name || selectedMedium} market shows ${mediumOverview.find(m => m.id === selectedMedium)?.change || 0 >= 0 ? 'positive' : 'negative'} momentum with ${Math.abs(mediumOverview.find(m => m.id === selectedMedium)?.change || 0)}% YoY change. Institutional interest remains strong across quality works.`,
    highlights: [
      'Contemporary art volume up 12.4% YoY, led by Street Art and Neo-Expressionism',
      'African Contemporary artists seeing record prices, with 42% average growth',
      'Asia-Pacific region showing strongest buyer activity growth at 15.8%',
      'Prints and editions segment outperforming with 12.8% growth',
    ],
    premiumInsights: [
      'Emerging artist index suggests potential breakout candidates in Figurative Revival',
      'Auction house pre-sale estimates trending conservative, creating arbitrage opportunities',
      'Private sale volume increasing 18% as collectors seek discretion for high-value works',
    ],
  }

  return (
    <div className="px-4 md:px-6 py-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="font-display text-2xl md:text-3xl font-light">Market Pulse</h1>
            <span className="text-xs px-2 py-1 rounded-full bg-positive/10 text-positive animate-pulse">
              Live
            </span>
          </div>
          <p className="text-text-secondary text-sm">
            Real-time auction intelligence across global markets
          </p>
        </div>

        {/* Time range selector */}
        <div className="flex items-center gap-2">
          {timeRanges.map((range) => (
            <button
              key={range.id}
              onClick={() => setTimeRange(range.id)}
              className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                timeRange === range.id
                  ? 'bg-text-primary text-white'
                  : 'bg-transparent border border-border text-text-secondary hover:border-border-hover'
              }`}
            >
              {range.name}
            </button>
          ))}
        </div>
      </div>

      {/* History limit warning */}
      {isHistoryLimited && timeRange === 'all' && (
        <div className="mb-6 px-4 py-3 rounded-xl border border-accent-primary/30 bg-accent-primary/5">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-accent-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <p className="text-sm text-text-primary flex-1">
              Free plan shows last 90 days only.{' '}
              <Link href="/get-started" className="font-medium text-accent-primary hover:underline">
                Upgrade for full 30-year history →
              </Link>
            </p>
          </div>
        </div>
      )}

      {/* Medium Filter Bar */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-text-tertiary uppercase tracking-wider">Filter by medium</span>
          {selectedMedium !== 'all' && (
            <button
              onClick={() => setSelectedMedium('all')}
              className="text-xs text-accent-primary hover:underline"
            >
              Clear filter
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedMedium('all')}
            className={`px-4 py-2 rounded-full text-sm transition-all flex items-center gap-2 ${
              selectedMedium === 'all'
                ? 'bg-text-primary text-white'
                : 'bg-card border border-border text-text-secondary hover:border-border-hover'
            }`}
          >
            All Mediums
          </button>
          {MEDIUMS.map((m) => (
            <button
              key={m.id}
              onClick={() => setSelectedMedium(m.id)}
              className={`px-4 py-2 rounded-full text-sm transition-all flex items-center gap-2 ${
                selectedMedium === m.id
                  ? 'bg-accent-primary text-white'
                  : 'bg-card border border-border text-text-secondary hover:border-border-hover'
              }`}
            >
              <MediumIcon medium={m.id} className="w-4 h-4" />
              {m.name}
            </button>
          ))}
        </div>
      </div>

      {/* AI Market Outlook - Expanded */}
      <div className="card p-6 mb-8 border-l-4" style={{ borderLeftColor: 'var(--accent-primary)' }}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent-primary)', color: 'white' }}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h2 className="font-medium text-lg">AI Market Outlook</h2>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  aiOutlook.sentiment === 'bullish' ? 'bg-positive/10 text-positive' : 'bg-negative/10 text-negative'
                }`}>
                  {aiOutlook.sentiment === 'bullish' ? '↑ Bullish' : '↓ Bearish'}
                </span>
                <span className="text-xs text-text-tertiary">{aiOutlook.confidence}% confidence</span>
              </div>
              {selectedMedium !== 'all' && (
                <span className="text-xs px-2 py-1 rounded-full bg-accent-primary/10 text-accent-primary">
                  {MEDIUMS.find(m => m.id === selectedMedium)?.name} focus
                </span>
              )}
            </div>

            <p className="text-sm text-text-secondary mb-4">{aiOutlook.summary}</p>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <h4 className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">Key Highlights</h4>
                <ul className="space-y-1.5">
                  {aiOutlook.highlights.map((highlight, i) => (
                    <li key={i} className="text-xs text-text-secondary flex items-start gap-2">
                      <span className="text-positive mt-0.5">•</span>
                      {highlight}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Premium Insights - Gated */}
              <div className={`relative ${isFreeUser ? 'pointer-events-none' : ''}`}>
                <h4 className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                  Premium Insights
                  {isFreeUser && <span className="ml-2 text-accent-primary">🔒</span>}
                </h4>
                <ul className={`space-y-1.5 ${isFreeUser ? 'blur-[6px] select-none' : ''}`}>
                  {aiOutlook.premiumInsights.map((insight, i) => (
                    <li key={i} className="text-xs text-text-secondary flex items-start gap-2">
                      <span className="text-accent-primary mt-0.5">★</span>
                      {insight}
                    </li>
                  ))}
                </ul>
                {isFreeUser && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Link
                      href="/get-started"
                      className="px-4 py-2 rounded-full text-sm font-medium bg-accent-primary text-white hover:opacity-90 transition-opacity"
                    >
                      View AI Insights →
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* By Era - Grid */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-medium text-lg">
            By Era
            {selectedMedium !== 'all' && (
              <span className="ml-2 text-sm font-normal text-text-tertiary">
                · {MEDIUMS.find(m => m.id === selectedMedium)?.name}
              </span>
            )}
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {filteredPeriodData.map((period) => (
            <Link
              key={period.id}
              href={`/app/market/${period.id}`}
              className="group card p-5 transition-all duration-300 cursor-pointer hover:shadow-lg hover:border-border-hover relative overflow-hidden"
            >
              <div
                className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: 'var(--accent-primary)' }}
              />

              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-medium text-base mb-0.5 group-hover:text-accent-primary transition-colors">
                    {period.name}
                  </h3>
                  <p className="text-text-tertiary text-xs">{period.dateRange}</p>
                </div>
              </div>

              <Sparkline data={period.data} positive={period.change >= 0} />

              <div className="mt-3 pt-3 border-t border-border-subtle">
                <div className="flex items-end justify-between">
                  <div>
                    <span className="text-lg font-display font-light">{period.volume}</span>
                    <p className="text-xs text-text-tertiary">{period.lots} lots</p>
                  </div>
                  <span
                    className="text-sm font-medium tabular-nums"
                    style={{ color: period.change >= 0 ? 'var(--positive)' : 'var(--negative)' }}
                  >
                    {period.change >= 0 ? '+' : ''}{period.change}%
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* By Medium - Overview Cards */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-medium text-lg">By Medium</h2>
          <span className="text-xs text-text-tertiary">Click to filter all data</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {mediumOverview.map((medium) => (
            <button
              key={medium.id}
              onClick={() => setSelectedMedium(selectedMedium === medium.id ? 'all' : medium.id)}
              className={`group card p-4 text-left transition-all duration-300 cursor-pointer hover:shadow-lg ${
                selectedMedium === medium.id ? 'border-accent-primary ring-1 ring-accent-primary' : 'hover:border-border-hover'
              }`}
            >
              <div className="flex items-center gap-2 mb-3">
                <MediumIcon
                  medium={medium.id}
                  className={`w-6 h-6 transition-colors ${
                    selectedMedium === medium.id ? 'text-accent-primary' : 'text-text-tertiary group-hover:text-text-secondary'
                  }`}
                />
                <span className="text-sm font-medium">{medium.name}</span>
              </div>

              <p className="text-xl font-display font-light mb-1">{medium.volume}</p>

              <div className="flex items-center justify-between">
                <span className="text-xs text-text-tertiary">{medium.share}% share</span>
                <span
                  className="text-xs font-medium tabular-nums"
                  style={{ color: medium.change >= 0 ? 'var(--positive)' : 'var(--negative)' }}
                >
                  {medium.change >= 0 ? '+' : ''}{medium.change}%
                </span>
              </div>

              <div className="w-full bg-page rounded-full h-1 mt-2">
                <div
                  className="h-1 rounded-full transition-all"
                  style={{
                    width: `${Math.min(medium.share * 1.5, 100)}%`,
                    background: selectedMedium === medium.id ? 'var(--accent-primary)' : 'var(--text-tertiary)'
                  }}
                />
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Two Column Layout: Upcoming Auctions + Recent Sales */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Upcoming Auctions */}
        <section className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium text-lg">Upcoming Auctions</h2>
            <Link href="/app/market" className="text-xs text-accent-primary hover:underline">
              View calendar →
            </Link>
          </div>

          <div className="space-y-3">
            {upcomingAuctions.slice(0, 5).map((auction, i) => (
              <div
                key={i}
                className={`py-3 border-b border-border-subtle last:border-0 ${
                  auction.featured ? 'bg-accent-primary/5 -mx-4 px-4 rounded-lg border-0 mb-2' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        auction.house === "Christie's"
                          ? 'bg-red-500/10 text-red-600'
                          : auction.house === "Sotheby's"
                          ? 'bg-blue-500/10 text-blue-600'
                          : auction.house === 'Phillips'
                          ? 'bg-purple-500/10 text-purple-600'
                          : 'bg-amber-500/10 text-amber-600'
                      }`}>
                        {auction.house}
                      </span>
                      <span className="text-xs text-text-tertiary">{auction.location}</span>
                      {auction.featured && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-accent-primary/10 text-accent-primary">Featured</span>
                      )}
                    </div>
                    <p className="text-sm font-medium truncate">{auction.title}</p>
                    <p className="text-xs text-text-tertiary">{auction.date} · {auction.lots} lots</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-medium tabular-nums">{auction.estimate}</p>
                    <p className="text-xs text-text-tertiary">Est.</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Set alert CTA */}
          <div className="mt-4 pt-4 border-t border-border-subtle">
            <button className="w-full py-2.5 rounded-xl border border-border text-text-secondary hover:border-accent-primary hover:text-accent-primary transition-colors text-sm font-medium flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              Set auction alerts
            </button>
          </div>
        </section>

        {/* Recent Notable Sales */}
        <section className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium text-lg">
              Recent Notable Sales
              {selectedMedium !== 'all' && (
                <span className="ml-2 text-sm font-normal text-text-tertiary">
                  · {MEDIUMS.find(m => m.id === selectedMedium)?.name}
                </span>
              )}
            </h2>
            <Link href="/app/market" className="text-xs text-accent-primary hover:underline">
              View all →
            </Link>
          </div>

          <div className="space-y-3">
            {filteredSales.slice(0, 5).map((sale) => (
              <Link
                key={sale.id}
                href={`/app/artists/${sale.artistSlug}`}
                className="flex items-center gap-3 py-2 hover:bg-page -mx-2 px-2 rounded-lg transition-colors"
              >
                <ArtistAvatar name={sale.artist} size="lg" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{sale.work}</p>
                  <p className="text-xs text-text-tertiary">{sale.artist} · {sale.house}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-medium tabular-nums">{sale.hammer}</p>
                  <p className={`text-xs tabular-nums ${sale.premium.startsWith('+') ? 'text-positive' : 'text-negative'}`}>
                    {sale.premium} vs est.
                  </p>
                </div>
              </Link>
            ))}
          </div>

          {filteredSales.length === 0 && (
            <p className="text-sm text-text-tertiary text-center py-8">
              No recent sales for this medium filter.
            </p>
          )}

          {/* Export CTA - Gated */}
          <div className="mt-4 pt-4 border-t border-border-subtle">
            <TierGate feature="export" showPrompt={false}>
              <button className="w-full py-2.5 rounded-xl border border-border text-text-secondary hover:border-accent-primary hover:text-accent-primary transition-colors text-sm font-medium flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export sales data
              </button>
            </TierGate>
          </div>
        </section>
      </div>

      {/* Geographic Insights */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-medium text-lg">Geographic Insights</h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {geographicData.map((geo) => (
            <Link
              key={geo.region}
              href={`/app/market/country/${geo.region.toLowerCase().replace(/[^a-z]/g, '-')}`}
              className="group card p-5 hover:shadow-lg hover:border-border-hover transition-all"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium group-hover:text-accent-primary transition-colors">{geo.region}</h3>
                <span className={`text-sm font-medium tabular-nums ${geo.change >= 0 ? 'text-positive' : 'text-negative'}`}>
                  {geo.change >= 0 ? '+' : ''}{geo.change}%
                </span>
              </div>
              <p className="text-2xl font-display font-light mb-2">{geo.volume}</p>
              <div className="w-full bg-page rounded-full h-2 mb-2">
                <div
                  className="h-2 rounded-full transition-all"
                  style={{ width: `${geo.share}%`, background: 'var(--accent-primary)' }}
                />
              </div>
              <p className="text-xs text-text-tertiary">
                {geo.share}% global · Top: {geo.topCities[0]}
              </p>
            </Link>
          ))}
        </div>

        {/* Regional trends */}
        <div className="card p-5">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-positive/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-positive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-sm">Asia-Pacific Surge</p>
                <p className="text-xs text-text-tertiary">Hong Kong and Singapore seeing record buyer registrations for Contemporary and Asian Modern.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-accent-primary/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-sm">Middle East Momentum</p>
                <p className="text-xs text-text-tertiary">UAE collectors increasingly active in blue-chip Contemporary, with Dubai as regional hub.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-accent-warm/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-accent-warm" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-sm">Americas Stable</p>
                <p className="text-xs text-text-tertiary">New York maintains dominance with 65% of regional volume, LA growing for Contemporary.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Upgrade CTA for free users */}
      {isFreeUser && (
        <section className="card p-6 bg-gradient-to-br from-accent-primary/5 to-accent-secondary/5 border-accent-primary/20">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1">
              <h3 className="font-display text-xl font-light mb-2">Get the full picture</h3>
              <p className="text-sm text-text-secondary mb-4">
                Get deeper market intelligence with 30-year historical data, AI-powered insights,
                and real-time alerts when your tracked artists go to auction.
              </p>
              <ul className="space-y-1">
                <li className="text-xs text-text-tertiary flex items-center gap-2">
                  <svg className="w-4 h-4 text-positive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  30+ years of auction history
                </li>
                <li className="text-xs text-text-tertiary flex items-center gap-2">
                  <svg className="w-4 h-4 text-positive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Unlimited AI insights & recommendations
                </li>
                <li className="text-xs text-text-tertiary flex items-center gap-2">
                  <svg className="w-4 h-4 text-positive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Export data to CSV/PDF
                </li>
              </ul>
            </div>
            <div className="flex flex-col gap-2">
              <Link
                href="/get-started"
                className="px-6 py-3 rounded-xl font-medium text-white text-center"
                style={{ background: 'var(--accent-primary)' }}
              >
                From $199/mo
              </Link>
              <span className="text-xs text-text-tertiary text-center">Cancel anytime</span>
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
